/**
 * authHubService — integration with the DDD Auth Hub.
 *
 * Profound Philosophy signs in exclusively through the hub (the fleet's
 * hub-first pattern; finding-your-voice is the reference implementation).
 * Token exchange signs the hub custom token into the hub Firebase app —
 * that session alone is the sign-in. The hub does not mint a spoke token
 * for this app (no SPOKE_CONFIG entry), and none is needed: per-user data
 * lives in the hub thin layer (hubAppState.js), not the app's own project.
 *
 * Australian English throughout.
 */

import { auth, authHubAuth } from './firebase';
import { signInWithCustomToken, signOut as firebaseSignOut } from 'firebase/auth';
import { getUrlSecret, clearUrlSecrets, HUB_URL_PARAMS } from '../utils/urlSecrets.js';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

/**
 * Deep-link callback for native builds. After authentication the hub redirects
 * the in-app browser here with the token on the URL fragment; iOS registers the
 * scheme in Info.plist (CFBundleURLTypes) and NativeDeepLinkHandler routes the
 * fragment into /auth/token-exchange.
 *
 * Must match the entry in the Auth Hub's `urlValidation.js` NATIVE_APP_CALLBACKS
 * allowlist EXACTLY — the hub compares the scheme://host/path as a lowercase
 * string, and a mismatch fails the return leg after a sign-in that appeared to
 * work. Pemberton lost two review rounds to faults in this leg.
 */
export const NATIVE_AUTH_CALLBACK = 'com.divedeepdialogues.profoundphilosophy://auth/callback';

class AuthHubService {
  constructor() {
    this.hubUrl = import.meta.env.VITE_AUTH_HUB_URL || 'https://auth.divedeepdialogues.com';
    this.appId = 'profound-philosophy';
    this.appName = 'Profound Philosophy';
  }

  isEnabled() {
    return import.meta.env.VITE_USE_AUTH_HUB === 'true' && !!authHubAuth;
  }

  /**
   * Open a hub URL. On the web this is a plain navigation. On native the
   * app's own webview must never leave the bundled app, so the hub opens in
   * the system in-app browser (SFSafariViewController) and comes back through
   * the deep-link callback.
   */
  _openHubUrl(url) {
    if (Capacitor.isNativePlatform()) {
      Browser.open({ url }).catch((error) =>
        console.error('[authHubService] Could not open in-app browser:', error)
      );
    } else {
      window.location.href = url;
    }
  }

  redirectToLogin(returnPath = null) {
    const returnUrl = this.buildReturnUrl(returnPath);
    this.storeReturnPath(returnPath);
    this._openHubUrl(`${this.hubUrl}/login?app=${this.appId}&return=${encodeURIComponent(returnUrl)}`);
  }

  redirectToSignup(returnPath = null) {
    const returnUrl = this.buildReturnUrl(returnPath);
    this.storeReturnPath(returnPath);
    this._openHubUrl(`${this.hubUrl}/signup?app=${this.appId}&return=${encodeURIComponent(returnUrl)}`);
  }

  /**
   * Web: an https URL back into this origin's token-exchange route, carrying
   * the post-auth destination as a query parameter.
   *
   * Native: the registered custom-scheme callback and nothing else. Under
   * Capacitor `window.location.origin` is `capacitor://localhost`, which the hub
   * rejects as an unknown return host, and the callback has to match the
   * allowlist byte for byte, so the destination travels in sessionStorage
   * (storeReturnPath) rather than on the URL.
   */
  buildReturnUrl(path = null) {
    if (Capacitor.isNativePlatform()) {
      return NATIVE_AUTH_CALLBACK;
    }
    const base = `${window.location.origin}/auth/token-exchange`;
    return path ? `${base}?redirect=${encodeURIComponent(path)}` : base;
  }

  storeReturnPath(path) {
    if (path) sessionStorage.setItem('pp_authReturnPath', path);
  }

  getAndClearReturnPath() {
    const path = sessionStorage.getItem('pp_authReturnPath');
    sessionStorage.removeItem('pp_authReturnPath');
    return path || '/dashboard';
  }

  /**
   * Tokens ride the URL fragment (preferred: never hits logs) or query, but
   * main.jsx lifts them into memory before first render (utils/urlSecrets.js),
   * so analytics never sees them. The URL reads are a fallback only.
   */
  _getFragmentParams() {
    return new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
  }

  hasAuthToken() {
    return !!this.getAuthToken();
  }

  getAuthToken() {
    const captured = getUrlSecret('token');
    if (captured) return captured;
    const fragment = this._getFragmentParams().get('token');
    if (fragment) return fragment;
    return new URLSearchParams(window.location.search).get('token');
  }

  /** Spoke-audience token, if the hub ever mints one for this app. */
  getSpokeToken() {
    const captured = getUrlSecret('spokeToken');
    if (captured) return captured;
    const fragment = this._getFragmentParams().get('spokeToken');
    if (fragment) return fragment;
    return new URLSearchParams(window.location.search).get('spokeToken');
  }

  getRedirectPath() {
    const params = new URLSearchParams(window.location.search);
    return params.get('redirect') || this.getAndClearReturnPath();
  }

  async handleTokenExchange() {
    const token = this.getAuthToken();
    if (!token) throw new Error('No auth token provided');
    const spokeToken = this.getSpokeToken();
    // Tokens are in hand: get them out of the address bar now, not after the
    // network round trips (and on failure too), then forget them once used.
    this.cleanUrl();
    try {
      return await this._exchange(token, spokeToken);
    } finally {
      clearUrlSecrets(HUB_URL_PARAMS);
    }
  }

  async _exchange(token, spokeToken) {
    // 1. Hub session — this is the sign-in, and what the hub data callables
    //    and the Knowledge Hub coaching rail authenticate with.
    let hubCredential = null;
    if (authHubAuth) {
      try {
        hubCredential = await signInWithCustomToken(authHubAuth, token);
      } catch (error) {
        console.error('[authHubService] Hub session sign-in failed:', error?.code || error);
      }
    }

    // 2. Own-project session, only when the hub supplied a spoke token.
    //    Without one, the hub session carries the sign-in alone.
    let spokeCredential = null;
    if (auth && spokeToken) {
      try {
        spokeCredential = await signInWithCustomToken(auth, spokeToken);
      } catch (error) {
        console.warn('[authHubService] Spoke sign-in failed (non-fatal):', error?.code || error);
      }
    }

    const credential = hubCredential || spokeCredential;
    if (!credential) throw new Error('No auth session could be established');

    return { user: credential.user };
  }

  cleanUrl() {
    const url = new URL(window.location.href);
    const fragment = this._getFragmentParams();
    if (!fragment.has('token') && !fragment.has('spokeToken')
      && !url.searchParams.has('token') && !url.searchParams.has('spokeToken')) return;
    url.searchParams.delete('token');
    url.searchParams.delete('spokeToken');
    url.searchParams.delete('app');
    url.hash = '';
    window.history.replaceState({}, '', url.toString());
  }

  async signOut() {
    if (authHubAuth) await firebaseSignOut(authHubAuth).catch(() => {});
    if (auth) await firebaseSignOut(auth).catch(() => {});
  }
}

export const authHubService = new AuthHubService();
export default authHubService;
